import React from 'react';
import { observer } from 'mobx-react';
import { action, observable, runInAction } from 'mobx';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ResultKind } from 'services/base/types';
import { HistoryService } from 'services/base/history';
import { INITIAL_NOTIFICATION, PAYMENT_REDIRECT, configureInitialLoadComplete } from 'services/base/config';
import { SessionService } from 'services/accounts/session_service';
import { AccountsDomainProps, withAccountsDomain } from 'services/accounts/accounts_domain';
import { BasePath, pagePathToPathString } from 'services/base/page_path';
import { Theme, ThemeProps, ThemedStyles, useStyles, withTheme } from 'ui/base/theme';
import baseStyles from './armadillo_loader.module.css';
import { loadAllPangolinDomains } from 'services/pangolin/loaders';
import { redirectByNotification } from 'app/notifications';
import { redirectByPayment } from 'app/payments';

type ArmadilloLoaderStyles = {
  loaderContainer: string;
  titleText: string;
  animationWrapper: string;
  logoContainer: string;
  mouth: string;
  nose: string;
  head: string;
  hair: string;
  leftEye: string;
  rightEye: string;
  titleContainer: string;
};

const themedStyles: ThemedStyles<ArmadilloLoaderStyles> = {
  [Theme.DAYLIGHT]: {
    loaderContainer: baseStyles.daylightLoaderContainer,
    titleText: baseStyles.daylightTitle,
    animationWrapper: baseStyles.animationWrapper,
    logoContainer: baseStyles.logoContainer,
    mouth: baseStyles.mouth,
    nose: baseStyles.nose,
    head: baseStyles.head,
    hair: baseStyles.hair,
    leftEye: baseStyles.leftEye,
    rightEye: baseStyles.rightEye,
    titleContainer: baseStyles.titleContainer,
  },
  [Theme.MIDNIGHT]: {
    loaderContainer: baseStyles.midnightLoaderContainer,
    titleText: baseStyles.midnightTitle,
    animationWrapper: baseStyles.animationWrapper,
    logoContainer: baseStyles.logoContainer,
    mouth: baseStyles.mouth,
    nose: baseStyles.nose,
    head: baseStyles.head,
    hair: baseStyles.hair,
    leftEye: baseStyles.leftEye,
    rightEye: baseStyles.rightEye,
    titleContainer: baseStyles.titleContainer,
  },
};

type PangolinAnimationProps = {
  onAnimationEnd: () => void;
};

const PangolinAnimation = ({ onAnimationEnd }: PangolinAnimationProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={baseStyles.animationWrapper}>
      <div className={baseStyles.logoContainer}>
        <div className={baseStyles.mouth}/>
        <div className={baseStyles.nose}/>
        <div className={baseStyles.head}/>
        <div className={baseStyles.hair}/>
        <div className={baseStyles.leftEye}/>
        <div className={baseStyles.rightEye}/>
      </div>
      <div className={baseStyles.titleContainer}>
        <div className={styles.titleText} onAnimationEnd={onAnimationEnd}/>
      </div>
    </div>
  );
};

export const publicRoutes = [
  pagePathToPathString({ kind: BasePath.INTRO }),
  pagePathToPathString({ kind: BasePath.REGISTER }),
  pagePathToPathString({ kind: BasePath.LOGIN }),
  pagePathToPathString({ kind: BasePath.PASSWORD_RESET }),
];

type BaseArmadilloLoaderProps = ThemeProps & AccountsDomainProps & RouteComponentProps & { children?: React.ReactNode };

@observer class BaseArmadilloLoader extends React.Component<BaseArmadilloLoaderProps> {
  @observable.ref loaded: boolean;
  @observable.ref animationEnded: boolean;
  @observable.ref animationSkipped: boolean;

  private clearListeners: (() => void) | undefined;
  private clearBlockers: (() => void) | undefined;
  private retryTimeout: number;

  constructor(props: BaseArmadilloLoaderProps) {
    super(props);
    this.loaded = false;
    this.animationEnded = false;
    this.animationSkipped = false;
    this.clearListeners = undefined;
    this.clearBlockers = undefined;
    this.retryTimeout = 500;
  }

  componentDidMount() {
    const location = this.props.location;
    const history = this.props.history;
    HistoryService.initialize(location);
    this.loadDataAndRedirect();
    this.clearListeners = history.listen(HistoryService.listener);
    this.clearBlockers = history.block(HistoryService.blocker);
  }

  componentWillUnmount() {
    if (this.clearListeners != null) {
      this.clearListeners();
    }
    if (this.clearBlockers != null) {
      this.clearBlockers();
    }
  }

  @action.bound
  async loadDataAndRedirect() {
    const history = this.props.history;
    const location = this.props.location;
    const accounts = this.props.accounts;

    const session = await SessionService.fetch(accounts);
    if (session.kind !== ResultKind.OK) {
      // If we fail to load, retry then double the retry time
      setTimeout(this.loadDataAndRedirect, this.retryTimeout);
      this.retryTimeout = Math.min(this.retryTimeout * 2, 20000);
      return;
    }

    if (session.authenticated) {
      if (INITIAL_NOTIFICATION != null) {
        await loadAllPangolinDomains({ fcm: INITIAL_NOTIFICATION });
      } else {
        await loadAllPangolinDomains({ url: new URL(window.location.href) });
      }
    }

    const pangolin = accounts.pangolins.getDefault();

    runInAction(() => {
      configureInitialLoadComplete(true);
      this.loaded = true;

      if (accounts.user != null) {
        if (accounts.user.emailVerifiedAt == null) {
          HistoryService.clear(history);
          HistoryService.replace(history, { kind: BasePath.EMAIL_VERIFICATION });
        } else if (pangolin == null) {
          HistoryService.clear(history);
          HistoryService.replace(history, { kind: BasePath.ADD_PANGOLIN_SETUP });
        } else if (INITIAL_NOTIFICATION != null) {
          redirectByNotification(history, INITIAL_NOTIFICATION);
        } else if (PAYMENT_REDIRECT != null) {
          redirectByPayment(history, PAYMENT_REDIRECT);
        } else if (publicRoutes.includes(location.pathname)) {
          HistoryService.clear(history);
          HistoryService.replace(history, {
            kind: BasePath.HOME,
            pangolinID: pangolin.id,
          });
        }
      } else if (!publicRoutes.includes(location.pathname)) {
        HistoryService.clear(history);
        HistoryService.replace(history, { kind: BasePath.INTRO });
      }
    });
  }

  @action.bound
  handleAnimationEnd() {
    this.animationEnded = true;
  }

  @action.bound
  handleAnimationSkip() {
    this.animationSkipped = true;
  }

  loader(): React.ReactNode {
    const styles = themedStyles[this.props.theme];

    return (
      <div className={styles.loaderContainer} onClick={this.handleAnimationSkip}>
        <PangolinAnimation onAnimationEnd={this.handleAnimationEnd}/>
      </div>
    );
  }

  render() {
    if (this.loaded && (this.animationEnded || this.animationSkipped)) {
      return <>{this.props.children}</>;
    } else {
      return this.loader();
    }
  }
}

export const ArmadilloLoader = withRouter(withAccountsDomain(withTheme(BaseArmadilloLoader)));
